/**
 * Lightweight replacement for TanStack Query's QueryClient.
 * Now enhanced with in-memory caching, staleTime support, and request deduplication
 * to prevent duplicate & excessive GET API requests across the application.
 */

export type QueryKey = readonly unknown[];

interface QuerySubscriber {
  /** Current, up-to-date query key for this mounted query. */
  key: QueryKey;
  /** Re-run the query fn (background refetch). */
  refetch: (force?: boolean) => void;
}

const subscribers = new Set<QuerySubscriber>();

interface CacheEntry<T = unknown> {
  data: T;
  timestamp: number;
}

const queryCache = new Map<string, CacheEntry>();
const inflightRequests = new Map<string, Promise<unknown>>();

/** Register a mounted query. Returns an unsubscribe fn. */
export const registerQuery = (subscriber: QuerySubscriber): (() => void) => {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
};

/** True when `filter` is a prefix of `key` (segment-by-segment deep equal). */
export const keyStartsWith = (key: QueryKey, filter: QueryKey): boolean => {
  if (filter.length > key.length) return false;
  for (let i = 0; i < filter.length; i += 1) {
    if (!deepEqual(key[i], filter[i])) return false;
  }
  return true;
};

/** Structural equality good enough for query-key segments (primitives/arrays/plain objects). */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const ka = Object.keys(a as Record<string, unknown>);
    const kb = Object.keys(b as Record<string, unknown>);
    if (ka.length !== kb.length) return false;
    return ka.every((k) =>
      deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    );
  }
  return false;
};

/** Retrieve cached result if within staleTime window */
export const getQueryCache = <T>(keyString: string, staleTimeMs: number): T | undefined => {
  const entry = queryCache.get(keyString);
  if (!entry) return undefined;
  if (Date.now() - entry.timestamp > staleTimeMs) {
    queryCache.delete(keyString);
    return undefined;
  }
  return entry.data as T;
};

/** Store query result in memory cache */
export const setQueryCache = <T>(keyString: string, data: T): void => {
  queryCache.set(keyString, { data, timestamp: Date.now() });
};

/** Deduplicate concurrent in-flight requests for identical keys */
export const fetchDeduplicated = async <T>(
  keyString: string,
  fetchFn: () => Promise<T>,
): Promise<T> => {
  const existing = inflightRequests.get(keyString);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = (async () => {
    try {
      const data = await fetchFn();
      setQueryCache(keyString, data);
      return data;
    } finally {
      inflightRequests.delete(keyString);
    }
  })();

  inflightRequests.set(keyString, promise);
  return promise;
};

/** Clear cached entries matching key prefix */
export const clearQueryCache = (queryKey?: QueryKey): void => {
  if (!queryKey) {
    queryCache.clear();
    return;
  }
  for (const k of queryCache.keys()) {
    try {
      const parsedKey = JSON.parse(k);
      if (Array.isArray(parsedKey) && keyStartsWith(parsedKey, queryKey)) {
        queryCache.delete(k);
      }
    } catch {
      // Ignore parse error
    }
  }
};

/**
 * Refetch every mounted query whose key is prefix-matched by `queryKey`.
 * Clears the query cache for invalidation targets so fresh data is fetched.
 */
export const invalidateQueries = ({ queryKey }: { queryKey: QueryKey }): void => {
  clearQueryCache(queryKey);
  subscribers.forEach((sub) => {
    if (keyStartsWith(sub.key, queryKey)) sub.refetch(true);
  });
};

export interface QueryClientLike {
  invalidateQueries: (filters: { queryKey: QueryKey }) => void;
}

const queryClient: QueryClientLike = { invalidateQueries };

/** Mirror of `useQueryClient()` — returns the shared client. */
export const useQueryClient = (): QueryClientLike => queryClient;
